import type { Message } from '@ai-sdk/ui-utils';
import { atom, computed, map, type MapStore, type ReadableAtom, type WritableAtom } from 'nanostores';
import { VERSORI_FLOW_FILE_NAME } from '~/utils/constants';
import { ActionRunner } from '../lib/action-runner';
import { type ActionCallbackData } from '../lib/message-parser';
import { type VersoriArtifactData } from '../types/artifact';
import { ChatStore } from './chat';
import { ConnectionStore } from './connection';
import { type EditorDocument, EditorDocuments, EditorStore } from './editor';
import { type Flow, type FlowStep, FlowStore } from './flow';
import { InterfaceStore, NavigationState } from './interface';
import { AnonProject, ProjectStore } from './project';

import {
  Activation,
  ConnectionCreate,
  ConnectionSummary,
  EndUser,
  Project,
  ConnectionTemplate,
  System,
} from '~/services/versori-api/types';
import { ActivationStore } from './activation';
import { EndUserStore } from './endUsers';
import { FilesStore } from './files';
import { SystemStore } from './systems';
import { ScrollPosition } from '~/components/CodeMirror/CodeMirror';
export interface ArtifactState {
  id: string;
  title: string;
  type: string;
  closed: boolean;
  runner: ActionRunner;
}

export type ArtifactUpdateState = Pick<ArtifactState, 'title' | 'closed'>;

type Artifacts = MapStore<Record<string, ArtifactState>>;

export class WorkspaceStore {
  artifacts: Artifacts = map({});

  artifactIdList: string[] = [];

  #filesStore = new FilesStore();

  #editorStore = new EditorStore(this.#filesStore);

  #projectStore = new ProjectStore();

  #interfaceStore = new InterfaceStore();

  #connectionStore = new ConnectionStore();

  #flowStore = new FlowStore();

  #chatStore = new ChatStore();

  #systemStore = new SystemStore();

  #activationStore = new ActivationStore();

  #endUserStore = new EndUserStore();

  unsavedFiles: WritableAtom<Set<string>> = atom(new Set<string>());
  fileMerge: MapStore<Record<string, string>> = map({});
  projectInitialised: MapStore<Record<string, boolean>> = map({
    availableConnections: false,
    connectionTemplates: false,
  });

  versoriFlowInProject = computed([this.files], (files) => {
    if (!files) {
      return undefined;
    }

    const versoriFlow = files[`${VERSORI_FLOW_FILE_NAME}`];

    if (!versoriFlow) {
      return undefined;
    } else {
      this.#interfaceStore.toggleView('flow');
    }

    return JSON.parse(versoriFlow.content);
  });

  // Used to check if critical information is present so UI can make decisions
  projectIsInitialised = computed([this.projectInitialised], (projectInitialised) => {
    return Object.values(projectInitialised).every((value) => value);
  });

  projectName = computed([this.project, this.files], (project, files) => {
    if (!project.name) {
      return undefined;
    }
    if (!project.name.startsWith('v-')) {
      return project.name;
    }
    const packageJson = files['package.json'];
    if (packageJson) {
      const content = JSON.parse(packageJson.content);
      if (project.name.startsWith('v-')) {
        return content.name;
      }
      return project.name;
    }
    return project.name;
  });

  get packageJson() {
    return this.#editorStore.packageJsonFile;
  }

  get interactionState() {
    return this.#interfaceStore.interactionState;
  }

  get initialPrompt() {
    return this.#chatStore.initialPrompt;
  }

  get chat() {
    return this.#chatStore.chat;
  }

  get file() {
    return this.#editorStore.selectedFile;
  }

  get files() {
    return this.#editorStore.files;
  }

  get workspaceFiles() {
    return this.#filesStore.workspaceFiles;
  }

  get fileMerges() {
    return this.fileMerge;
  }

  get currentDocument(): ReadableAtom<EditorDocument | undefined> {
    return this.#editorStore.currentDocument;
  }

  get associatedWorkspaceFile(): ReadableAtom<EditorDocument | undefined> {
    return this.#editorStore.associatedWorkspaceFile;
  }

  get project() {
    return this.#projectStore.project;
  }

  get authenticationSidebar() {
    return this.#interfaceStore.authenticationSidebar;
  }

  get isDockOpen() {
    return this.#interfaceStore.dockOpen;
  }

  get preAuthenticateSystems() {
    return this.#interfaceStore.preAuthenticateSystems;
  }

  get systemManagementSidebar() {
    return this.#interfaceStore.systemManagementSidebar;
  }

  get viewState() {
    return this.#interfaceStore.viewState;
  }

  get showUnsavedChangesModal() {
    return this.#interfaceStore.showUnsavedChangesModal;
  }

  get markdownFile(): ReadableAtom<string | undefined> {
    return this.#editorStore.markdownFile;
  }

  versoriFlowJsonFile = computed([this.#editorStore.files], (files) => {
    if (!files) return undefined;

    const flowFile = files[`${VERSORI_FLOW_FILE_NAME}`];
    if (!flowFile) return undefined;

    return flowFile;
  });

  get activeFlowStep() {
    return this.#flowStore.activeFlowStep;
  }

  get flowStepActive() {
    return this.#flowStore.flowStepActive;
  }

  get flow() {
    return this.#flowStore.flow;
  }

  get updateMessage() {
    return this.#chatStore.updateMessage;
  }

  get messageReady() {
    return this.#chatStore.messageReady;
  }

  get fixDeploymentAttempts() {
    return this.#interfaceStore.fixDeploymentAttempts;
  }

  get activeDockTab() {
    return this.#interfaceStore.activeDockTab;
  }

  get pendingNavigation() {
    return this.#interfaceStore.pendingNavigation;
  }

  get isDeploying() {
    return this.#interfaceStore.isDeploying;
  }

  get isSaving() {
    return this.#interfaceStore.isSaving;
  }

  get filesSize() {
    return this.#editorStore.size;
  }

  get systemsSize() {
    return this.#systemStore.size;
  }

  get promptTemplate() {
    return this.#interfaceStore.promptTemplate;
  }

  get kickStartChat() {
    return this.#chatStore.kickStartChat;
  }

  get runTimeSystems() {
    return this.#systemStore.runTimeSystems;
  }

  get isStreaming() {
    return this.#interfaceStore.isStreaming;
  }

  getfileMergeState(filePath: string) {
    return this.fileMerge.get()[filePath];
  }

  setKickStartChat(value: boolean) {
    this.#chatStore.setKickStartChat(value);
  }

  togglePromptTemplate(value: boolean) {
    this.#interfaceStore.togglePromptTemplate(value);
  }

  setInteractionState(value: 'projectOnboarding' | 'chat') {
    this.#interfaceStore.toggleInteractionState(value);
  }

  closePreAuthenticateSystems() {
    this.#interfaceStore.toggleSidebar(false);
    this.#interfaceStore.togglePreAuthenticateSystems(false);
    this.#interfaceStore.toggleSystemManagement(false);
    this.#systemStore.setCurrentConnectionTemplateInManagement(null);
  }

  setPreAuthenticateSystems(dynamicDataSystems: boolean) {
    this.#interfaceStore.togglePromptTemplate(false);
    this.#interfaceStore.toggleSidebar(true);
    this.#interfaceStore.togglePreAuthenticateSystems(true);
    this.#systemStore.setDynamicDataSystems(dynamicDataSystems);
    this.#interfaceStore.toggleSystemManagement(false);
    this.#systemStore.setCurrentConnectionTemplateInManagement(null);
  }

  get dynamicDataSystems() {
    return this.#systemStore.dynamicDataSystems;
  }

  get availableConnections() {
    return this.#connectionStore.availableConnections;
  }

  get organisationConnections() {
    return this.#connectionStore.organisationConnections;
  }

  get selectedUserConnections() {
    return this.#connectionStore.selectedUserConnections;
  }

  get organisationSystemConnections() {
    return this.#connectionStore.organisationSystemConnections;
  }

  get connectionTemplates() {
    return this.#systemStore.connectionTemplates;
  }

  get activations() {
    return this.#activationStore.activations;
  }

  get currentConnectionTemplateInManagement() {
    return this.#systemStore.currentConnectionTemplateInManagement;
  }

  get currentSystemManagementView() {
    return this.#systemStore.currentSystemManagementView;
  }

  get currentSystemInManagement() {
    return this.#systemStore.currentSystemInManagement;
  }

  get systemAuthenticationSidebar() {
    return this.#interfaceStore.systemAuthenticationSidebar;
  }

  get aiModifiedFiles() {
    return this.#editorStore.aiModifiedFiles;
  }

  get projectInfoTab() {
    return this.#interfaceStore.projectInfoTab;
  }

  resetAiModifiedFiles() {
    this.#editorStore.resetAiModifiedFiles();
  }

  setActivation(activation: Activation) {
    this.#activationStore.setActivation(activation);
  }

  setActivations(activations: Activation[]) {
    this.#activationStore.setActivations(activations);
  }

  resetActivations() {
    this.#activationStore.reset();
  }

  get endUsers() {
    return this.#endUserStore.users;
  }

  setEndUsers(users: EndUser[]) {
    this.#endUserStore.setUsers(users);
  }

  setConnectionTemplates(connectionTemplates: ConnectionTemplate[]) {
    this.#systemStore.setConnectionTemplates(connectionTemplates);
    this.projectInitialised.setKey('connectionTemplates', true);
  }

  getFileModifications() {
    return this.#editorStore.getFileModifications();
  }

  setInitialPrompt(prompt: string | undefined) {
    this.#chatStore.setInitialPrompt(prompt);
  }

  setProject(project: Project) {
    this.#projectStore.setProject(project);
  }

  addArtifact({ title, id, type }: VersoriArtifactData) {
    const artifact = this.#getArtifact(id);

    if (artifact) {
      return;
    }

    if (!this.artifactIdList.includes(id)) {
      this.artifactIdList.push(id);
    }

    this.artifacts.setKey(id, {
      id,
      title,
      type,
      closed: false,
      runner: new ActionRunner(),
    });
  }

  updateArtifact({ id }: VersoriArtifactData, state: Partial<ArtifactUpdateState>) {
    const artifact = this.#getArtifact(id);

    if (!artifact) {
      return;
    }

    this.artifacts.setKey(id, { ...artifact, ...state });
  }

  async addAction(data: ActionCallbackData) {
    const { artifactId } = data;

    const artifact = this.#getArtifact(artifactId);

    if (!artifact) {
      throw new Error('Artifact not found');
    }

    artifact.runner.addAction(data);
  }

  async runAction(data: ActionCallbackData) {
    const { artifactId } = data;

    const artifact = this.#getArtifact(artifactId);

    if (!artifact) {
      throw new Error('Artifact not found');
    }

    artifact.runner.runAction(data);
  }

  addWorkspaceFiles({ files }: { files: EditorDocuments }) {
    this.#filesStore.addWorkspaceFiles(files);
  }

  saveMessages(messages: Message[]) {
    this.#chatStore.saveMessages(messages);
  }

  addWorkspaceFile(filePath: string, file: EditorDocument) {
    this.#filesStore.addWorkspaceFile(filePath, file);
  }

  addFile({ filePath, content, fromDB }: { filePath: string; content: string; fromDB: boolean }) {
    this.#editorStore.addFile(filePath, content);
    if (fromDB) {
      return;
    }

    const previousUnsavedFiles = this.unsavedFiles.get();

    const newUnsavedFiles = new Set(previousUnsavedFiles);
    newUnsavedFiles.add(filePath);
    this.unsavedFiles.set(newUnsavedFiles);
  }

  removeFile(filePath: string) {
    this.#editorStore.removeFile(filePath);
  }

  selectFile(filePath: string | undefined) {
    this.#editorStore.selectFile(filePath);
  }

  setCurrentDocumentScrollPosition(position: ScrollPosition) {
    const editorDocument = this.currentDocument.get();

    if (!editorDocument) {
      return;
    }

    const { filePath } = editorDocument;

    this.#editorStore.updateScrollPosition(filePath, position);
  }

  setCurrentDocumentContent(newContent: string) {
    const filePath = this.currentDocument.get()?.filePath;

    if (!filePath) {
      return;
    }

    const originalContent = this.#filesStore.getWorkspaceFile(filePath)?.content;
    const unsavedChanges = originalContent !== undefined && originalContent !== newContent;

    // Only update the file if there's an actual change
    if (unsavedChanges) {
      this.#editorStore.updateFile(filePath, newContent);
      this.#editorStore.setModifiedFile(filePath, originalContent);

      const previousUnsavedFiles = this.unsavedFiles.get();
      const newUnsavedFiles = new Set(previousUnsavedFiles);
      newUnsavedFiles.add(filePath);
      this.unsavedFiles.set(newUnsavedFiles);
    } else {
      // If there are no changes, remove from unsaved files
      const previousUnsavedFiles = this.unsavedFiles.get();
      if (previousUnsavedFiles.has(filePath)) {
        const newUnsavedFiles = new Set(previousUnsavedFiles);
        newUnsavedFiles.delete(filePath);
        this.unsavedFiles.set(newUnsavedFiles);
      }
    }
  }

  setAiModifiedFile(filePath: string, isModified: boolean) {
    this.#editorStore.setAiModifiedFile(filePath, isModified);
  }

  rejectFile(filePath: string) {
    this.#editorStore.rejectFile(filePath);
    const previousUnsavedFiles = this.unsavedFiles.get();
    const newUnsavedFiles = new Set(previousUnsavedFiles);
    newUnsavedFiles.delete(filePath);
    this.unsavedFiles.set(newUnsavedFiles);
    this.fileMerge.setKey(filePath, 'reject');
  }

  acceptFile(filePath: string) {
    // this.#editorStore.acceptFile(filePath);
    this.fileMerge.setKey(filePath, 'accept');
  }

  updateFile(filePath: string, newContent: string) {
    this.#editorStore.updateFile(filePath, newContent);
  }

  resetAllFiles() {
    this.unsavedFiles.set(new Set<string>());
  }

  toggleSidebar(value: boolean) {
    this.#interfaceStore.toggleSidebar(value);
  }

  toggleSystemManagement(value: boolean) {
    this.#interfaceStore.toggleSystemManagement(value);
  }

  toggleSystemAuthentication(value: boolean) {
    this.#interfaceStore.toggleSystemAuthentication(value);
  }

  setCurrentSystemInManagement(system: System | null) {
    this.#systemStore.setCurrentSystemInManagement(system);
  }

  setCurrentConnectionTemplateInManagement(connectionTemplate: ConnectionTemplate | null) {
    this.#systemStore.setCurrentConnectionTemplateInManagement(connectionTemplate);
  }

  setCurrentSystemManagementView(view: 'configure' | 'add' | 'connections' | 'none') {
    this.#systemStore.setCurrentSystemManagementView(view);
  }

  toggleView(value: 'editor' | 'flow' | 'diagram') {
    this.#interfaceStore.toggleView(value);
  }

  #getArtifact(id: string) {
    const artifacts = this.artifacts.get();
    return artifacts[id];
  }

  setActiveFlowStep({ step, position }: { step: FlowStep | null; position: { x: number; y: number } | null }) {
    this.#flowStore.setActiveFlowStep({ step, position });
  }

  addFlowStep(step: FlowStep) {
    this.#flowStore.addFlowStep(step);
  }

  addFlow(flow: Flow) {
    this.#flowStore.addFlow(flow);
  }

  setShowUnsavedChangesModal(value: boolean) {
    this.#interfaceStore.setShowUnsavedChangesModal(value);
  }

  onCreateConnection(organisationId: string, connection: ConnectionCreate, connectionTemplateId: string) {
    return this.#connectionStore.onCreateConnection(organisationId, connection, connectionTemplateId);
  }

  onAppendMessage({
    message,
    directive,
    role,
    type,
    resetDeploymentAttempts,
  }: {
    message: string;
    directive?: string;
    role?: 'user' | 'system' | 'data';
    type?: string;
    resetDeploymentAttempts?: boolean;
  }) {
    this.#chatStore.onAppendMessage({
      role,
      message,
      directive,
      type,
      resetDeploymentAttempts,
    });
  }

  onMessageReady(value: boolean) {
    this.#chatStore.onMessageReady(value);
  }

  incrementFixDeploymentAttempts() {
    this.#interfaceStore.incrementFixDeploymentAttempts();
  }

  resetFixDeploymentAttempts() {
    this.#interfaceStore.resetFixDeploymentAttempts();
  }

  setIsDockOpen(value: boolean) {
    this.#interfaceStore.setIsDockOpen(value);
  }

  setActiveDockTab(value: string) {
    this.#interfaceStore.setActiveDockTab(value);
  }

  workspaceReset() {
    this.artifacts.set({} as Record<string, ArtifactState>);
    this.artifactIdList = [];
    this.unsavedFiles.set(new Set<string>());
  }

  resetAllFileModifications() {
    this.#editorStore.resetFileModifications();
  }

  setPendingNavigation(value: NavigationState | null) {
    this.#interfaceStore.setPendingNavigation(value);
  }

  setIsDeploying(value: boolean) {
    this.#interfaceStore.setIsDeploying(value);
  }

  setIsSaving(value: boolean) {
    this.#interfaceStore.setIsSaving(value);
  }

  setAvailableConnections(connections: ConnectionSummary[]) {
    this.#connectionStore.setAvailableConnections(connections);
    this.projectInitialised.setKey('availableConnections', true);
  }

  setOrganisationSystemConnections(systemName: string, connections: ConnectionSummary[]) {
    this.#connectionStore.setOrganisationSystemConnections(systemName, connections);
  }

  setSelectedUserConnections(userId: string, connections: ConnectionSummary[]) {
    this.#connectionStore.setSelectedUserConnections(userId, connections);
  }

  setOrganisationConnections(connections: ConnectionSummary[]) {
    this.#connectionStore.setOrganisationConnections(connections);
  }

  hasUnsavedChanges() {
    // Check if there are any unsaved changes in the editor
    const files = this.#editorStore.files.get();

    // Compare current document contents with original file contents
    for (const [filePath, document] of Object.entries(files)) {
      const originalFile = files[filePath];
      if (originalFile && originalFile.type === 'file' && originalFile.content !== document?.content) {
        return true;
      }
    }

    return false;
  }

  setRunTimeSystems(value: boolean) {
    this.#systemStore.setRunTimeSystems(value);
  }

  toggleIsStreaming(value: boolean) {
    this.#interfaceStore.toggleIsStreaming(value);
  }

  toggleProjectInfoTab(value: boolean) {
    this.#interfaceStore.toggleProjectInfoTab(value);
  }

  reset() {
    this.workspaceReset();
    this.#editorStore.reset();
    this.#filesStore.reset();
    this.#interfaceStore.reset();
    this.#chatStore.reset();
    this.unsavedFiles.set(new Set<string>());
    this.#projectStore.reset();
    this.#systemStore.reset();
  }

  get architecturalDiagramFile(): ReadableAtom<EditorDocument | undefined> {
    return this.#editorStore.architecturalDiagramFile;
  }

  get transformationMappingFile(): ReadableAtom<EditorDocument | undefined> {
    return this.#editorStore.transformationMappingFile;
  }

  get isChatCollapsed() {
    return this.#interfaceStore.isChatCollapsed;
  }

  toggleInteractionCollapse() {
    this.#interfaceStore.toggleInteractionCollapse();
  }

  // Helper method to append research completion message with final report option
  onAppendResearchCompletionMessage(message: string) {
    this.onAppendMessage({
      message,
      type: 'researchComplete',
      directive: 'noResponse',
      role: 'data',
      resetDeploymentAttempts: false,
    });
  }
}

export const workspaceStore = new WorkspaceStore();
